import { jobMailSubscriptionstatus, jobMailSubscriptionToggleMutation } from 'components/form/api/user-admin.api';
import Grid from 'components/UI/atoms/Grid';
import Text from 'components/UI/atoms/Text';
import ToggleSwitch from 'components/UI/atoms/ToggleSwitch';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import Swal from 'sweetalert2';

/**
 * @alias 이메일 수신 토글 컴포넌트 (공통)
 */
export default function UserAdminJobMail() {
  const gmptoken = JSON.parse(sessionStorage.getItem('wakeupInfo') || '{}').gmpToken;
  // 이메일 수신
  const [emailReceive, setEmailReceive] = useState<boolean>(true);

  /**
   * @alias 이메일 수신 토글 상태 조회
   */
  const { data: jobMailSubscriptionStatus, isLoading: isJobMailSubscriptionStatusLoading } = useQuery<boolean>({
    queryKey: ['jobMailSubscriptionStatus'],
    queryFn: async () => {
      const response = await jobMailSubscriptionstatus();
      return response.result;
    },
    keepPreviousData: true,
    retry: false,
    enabled: !!gmptoken,
  });

  /**
   * @alias 응답값 오면 업데이트
   */
  useEffect(() => {
    if (!isJobMailSubscriptionStatusLoading && jobMailSubscriptionStatus !== undefined) {
      setEmailReceive(jobMailSubscriptionStatus);
    }
  }, [isJobMailSubscriptionStatusLoading, jobMailSubscriptionStatus]);

  /**
   * @alias 이메일 수신 토글 뮤테이션
   */
  const { mutate: jobMailSubscriptionToggleMutate } = useMutation(jobMailSubscriptionToggleMutation, {
    onSuccess: async (data) => {
      setEmailReceive(data.jobMailYn);
      await Swal.fire({
        icon: 'success',
        text: data.jobMailYn ? `이메일 수신이 활성화되었습니다.` : `이메일 수신이 비활성화되었습니다.`,
      });
    },
    onError: async (error: any) => {
      const errorMessage = error.response?.data?.message || '이메일 수신 토글 실패';
      await Swal.fire({
        title: '실패',
        icon: 'error',
        html: `${errorMessage}`,
      });
    },
  });

  /**
   * @alias 이메일 수신 토글 핸들러
   */
  const handleToggle = async () => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: '이메일 수신 설정 변경',
      html: emailReceive
        ? '연동 상품 업데이트 현황을 이메일로 받지 않습니다.<br/>이메일 수신 설정을 변경하시겠습니까?'
        : '연동 상품 업데이트 현황을 이메일로 받습니다.<br/>이메일 수신 설정을 변경하시겠습니까?',
      showCancelButton: true,
      cancelButtonText: '취소',
      confirmButtonText: '확인',
    });

    if (confirm.isConfirmed) {
      jobMailSubscriptionToggleMutate();
    }
  };

  return (
    <Grid display="flex" direction="row" gap={6} justifyContent="flex-start" alignItems="center">
      <Grid display="inline-flex" alignItems="center" direction="row" justifyContent="flex-start" gap={2}>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M15.8333 10.8333H14.1667C13.9306 10.8333 13.7326 10.7535 13.5729 10.5938C13.4132 10.434 13.3333 10.2361 13.3333 10C13.3333 9.7639 13.4132 9.56598 13.5729 9.40626C13.7326 9.24653 13.9306 9.16667 14.1667 9.16667H15.8333C16.0694 9.16667 16.2674 9.24653 16.4271 9.40626C16.5868 9.56598 16.6667 9.7639 16.6667 10C16.6667 10.2361 16.5868 10.434 16.4271 10.5938C16.2674 10.7535 16.0694 10.8333 15.8333 10.8333ZM12.1667 14C12.3056 13.8056 12.4861 13.6945 12.7083 13.6667C12.9306 13.6389 13.1389 13.6945 13.3333 13.8333L14.6667 14.8333C14.8611 14.9722 14.9722 15.1528 15 15.375C15.0278 15.5972 14.9722 15.8056 14.8333 16C14.6944 16.1945 14.5139 16.3056 14.2917 16.3333C14.0694 16.3611 13.8611 16.3056 13.6667 16.1667L12.3333 15.1667C12.1389 15.0278 12.0278 14.8472 12 14.625C11.9722 14.4028 12.0278 14.1945 12.1667 14ZM14.6667 5.16667L13.3333 6.16667C13.1389 6.30556 12.9306 6.36112 12.7083 6.33334C12.4861 6.30556 12.3056 6.19445 12.1667 6.00001C12.0278 5.80556 11.9722 5.59723 12 5.37501C12.0278 5.15278 12.1389 4.97223 12.3333 4.83334L13.6667 3.83334C13.8611 3.69445 14.0694 3.6389 14.2917 3.66667C14.5139 3.69445 14.6944 3.80556 14.8333 4.00001C14.9722 4.19445 15.0278 4.40278 15 4.62501C14.9722 4.84723 14.8611 5.02778 14.6667 5.16667ZM5.83333 12.5H3.33333C3.09722 12.5 2.89931 12.4201 2.73958 12.2604C2.57986 12.1007 2.5 11.9028 2.5 11.6667V8.33334C2.5 8.09723 2.57986 7.89931 2.73958 7.73959C2.89931 7.57987 3.09722 7.50001 3.33333 7.50001H5.83333L8.58333 4.75001C8.84722 4.48612 9.14931 4.42709 9.48958 4.57292C9.82986 4.71876 10 4.97917 10 5.35417V14.6458C10 15.0208 9.82986 15.2813 9.48958 15.4271C9.14931 15.5729 8.84722 15.5139 8.58333 15.25L5.83333 12.5Z"
            fill="#555555"
          />
        </svg>
        <Text fontSize="14px" fontWeight={500} color="#555" lineHeight={18}>
          매일 1회, 연동 상품 업데이트 현황을 이메일 받으시겠습니까?
        </Text>
      </Grid>
      <Text fontSize="14px" fontWeight={600} color="#0072CE" lineHeight={18}>
        이메일 수신
      </Text>
      <ToggleSwitch isChecked={emailReceive} onChange={handleToggle} width="36px" height="20px" circleWidth="16px" circleHeight="16px" sliderColor="#0072CE" />
    </Grid>
  );
}
